/**
 * Standalone conversation page for notification deep links.
 * Route: /app/conversation?conversation_id=:id
 */

import { useSearchParams } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@services/auth';
import { getConversationById, type Conversation } from '@services/conversations';
import ConversationThread from '@components/ConversationThread';
import BackButton from '../shared/BackButton';
import { useI18n } from '../i18n';
import { useAdminViewAs, getMessagingActingUserId } from '../lib/adminViewAs';

export default function ConversationPage() {
	const [searchParams] = useSearchParams();
	const { user } = useAuth();
	const { viewAsTarget } = useAdminViewAs();
	const actingUserId = useMemo(() => getMessagingActingUserId(user, viewAsTarget), [user, viewAsTarget]);
	const { language } = useI18n();
	const copy = (en: string, de: string, hr: string) =>
		language === 'de' ? de : language === 'hr' ? hr : en;
	const conversationId = searchParams.get('conversation_id');
	const [conversation, setConversation] = useState<Conversation | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (!conversationId || actingUserId == null) {
			if (actingUserId == null) setError(copy('Please log in to view this conversation.', 'Bitte melden Sie sich an, um diese Unterhaltung zu sehen.', 'Prijavite se za prikaz ovog razgovora.'));
			else if (!conversationId) setError(copy('Missing conversation.', 'Unterhaltung fehlt.', 'Nedostaje razgovor.'));
			setLoading(false);
			return;
		}
		const id = parseInt(conversationId, 10);
		if (Number.isNaN(id)) {
			setError(copy('Invalid conversation.', 'Ungültige Unterhaltung.', 'Nevažeći razgovor.'));
			setLoading(false);
			return;
		}
		const userId = actingUserId;
		if (Number.isNaN(userId)) {
			setError(copy('Invalid user.', 'Ungültiger Benutzer.', 'Nevažeći korisnik.'));
			setLoading(false);
			return;
		}
		setLoading(true);
		setError(null);
		getConversationById(id, userId)
			.then(setConversation)
			.catch((e) => setError(e instanceof Error ? e.message : copy('Failed to load conversation.', 'Unterhaltung konnte nicht geladen werden.', 'Neuspjelo učitavanje razgovora.')))
			.finally(() => setLoading(false));
	}, [conversationId, actingUserId, language]);

	if (!user) {
		return (
			<div className="container py-4">
				<p className="text-muted">{copy('Please log in to view conversations.', 'Bitte melden Sie sich an, um Unterhaltungen zu sehen.', 'Prijavite se za prikaz razgovora.')}</p>
			</div>
		);
	}

	if (loading) {
		return (
			<div className="container py-4">
				<div className="d-flex align-items-center gap-2">
					<BackButton fallbackPath="/app/dashboard" label={copy('Back', 'Zurück', 'Natrag')} size="sm" />
				</div>
				<div className="text-center py-5">
					<div className="spinner-border text-success" role="status">
						<span className="visually-hidden">{copy('Loading...', 'Laden...', 'Učitavanje...')}</span>
					</div>
					<p className="text-muted mt-2 mb-0">{copy('Loading conversation...', 'Unterhaltung wird geladen...', 'Učitavanje razgovora...')}</p>
				</div>
			</div>
		);
	}

	if (error || !conversation) {
		return (
			<div className="container py-4">
				<div className="d-flex align-items-center gap-2">
					<BackButton fallbackPath="/app/dashboard" label={copy('Back', 'Zurück', 'Natrag')} size="sm" />
				</div>
				<div className="alert alert-warning mt-3 mb-0">
					{error ?? copy('Conversation not found.', 'Unterhaltung nicht gefunden.', 'Razgovor nije pronađen.')}
				</div>
			</div>
		);
	}

	const currentUserId = actingUserId as number;
	const variant = conversation.project_id ? 'project' : 'offer';
	const offerId = conversation.offer_id;
	const projectId = conversation.project_id ?? undefined;

	return (
		<div className="container py-4">
			<div className="d-flex align-items-center gap-2 mb-3">
				<BackButton fallbackPath="/app/dashboard" label={copy('Back', 'Zurück', 'Natrag')} size="sm" />
			</div>
			<div className="card shadow-sm">
				<div className="card-body">
					<ConversationThread
						variant={variant}
						offerId={offerId}
						projectId={projectId}
						currentUserId={currentUserId}
						title={copy('Messages', 'Nachrichten', 'Poruke')}
					/>
				</div>
			</div>
		</div>
	);
}
